// צבעי לוח שנה (הגדרות ארגון → צבעי לוח שנה): the calendar paints events by their status and
// meetings by their meeting type.
//
// Every calendar item carries stable color classes (item-mapper.js):
//   events    pcSys_<systemStatusKey>, plus pcEventStatus_<planup_event_status.id> when it has one
//   meetings  pcMeeting, plus pcMeetingType_<planup_meeting_type.id> when it has one
//   tasks     pcTask
//   אישי      pcPersonal, plus pcPersonalUser_<users.id> (whose)
// and applyCalendarColors writes ONE <style> with a rule per color (App.vue watches the settings),
// so a color changed in the settings repaints every open calendar at once, with no refetch.
//
// How a color reaches an item: every calendar (PlanupCalendar's root, class `pcal`) draws its items
// - the month pills (.pcal-item), the week / day blocks (.timeSlotElement), the day popup's type chips
// and the toolbar's filter chips - from CSS custom properties:
//   --pc-rgb           the hue, as "r, g, b" (every tint is rgba(var(--pc-rgb), alpha))
//   --pc-ink           the text color: the hue, darkened until it reads on its own tint
//   --pc-alpha         the fill's alpha (.13 = a tinted pill, 0 = an outlined one)
//   --pc-border-alpha  the outline's alpha (0 = no outline)
//   --pc-stripe        1 = a striped fill (יצאה הזמנה nobody colored)
// PlanupCalendar.vue's stylesheet sets the defaults per item type and status (DEFAULT_LOOKS below);
// a color chosen in the settings overrides them with !important, and precedence comes from rule ORDER
// at equal specificity: pcEventStatus_* is written after pcSys_*, pcMeetingType_* after pcMeeting.
// The crew-members toggle adds a strip of the crew colors to the pill, the status color stays.
//
// Where each color lives (business_setting):
//   אירוע סגור (hazmanaSigned)    calendarEventColor
//   the other system statuses      calendarColors.systemStatus[key]
//   סטטוס תהליך פנימי              calendarColors.eventStatus[id]   - wins over the system status
//   meetings                       calendarMeetingColor; calendarColors.meetingType[id] wins
//   tasks                          calendarTaskColor
//   אישי (personal items)          calendarColors.other.personal; calendarColors.personalUser[users.id] wins
// A status with no color keeps the calendar's own look (DEFAULT_LOOKS); a color set here fills the
// item with its tint, in every view.

export const DEFAULT_EVENT_COLOR = '#e3185d';
export const DEFAULT_MEETING_COLOR = '#66dabd';
export const DEFAULT_TASK_COLOR = '#17b3c7';

// the hue of an event nobody colored while it is still a פניה (outlined / striped): the app's lavender
export const PENDING_EVENT_COLOR = '#ab8ce4';
// פניה חדשה (no system status), holidays, google events and the online-booking windows
export const NEW_LEAD_COLOR = '#d946ef';
export const HOLIDAY_COLOR = '#f5b400';
export const GOOGLE_EVENT_COLOR = '#94a3d3';
export const BOOKING_COLOR = '#2fb57a';
// אישי (מילואים / רופא / חופשה - item type 'personal'): a warm gray, the one thing on the calendar
// that is not business; calendarColors.other.personal overrides it
export const DEFAULT_PERSONAL_COLOR = '#78716c';

// the system statuses an event can have on the calendar, in the funnel's order. EventService.findBetweenDates
// leaves out פניה לא רלוונטית / הזמנה מבוטלת, and with them every event with no system status (NOT IN
// is never true for NULL) - so פניה חדשה never gets there either.
export const CALENDAR_SYSTEM_STATUSES = ['pniaInTipul', 'priceOfferSent', 'hazmanaSent', 'hazmanaSigned'];

// how the calendar draws a status nobody colored: a פניה in process / with a price offer is outlined
// in lavender, יצאה הזמנה is striped lavender, אירוע סגור is tinted with calendarEventColor's default
export const DEFAULT_LOOKS = {
    pniaInTipul: { outline: PENDING_EVENT_COLOR },
    priceOfferSent: { outline: PENDING_EVENT_COLOR },
    hazmanaSent: { stripes: PENDING_EVENT_COLOR },
    hazmanaSigned: { fill: DEFAULT_EVENT_COLOR },
};

// the icon and the Hebrew name of each item type (the pills, the hover card, the day popup)
export const ITEM_ICONS = {
    event: 'fa-solid fa-champagne-glasses',
    meeting: 'fa-regular fa-handshake',
    task: 'fa-solid fa-bullseye',
    holiday: 'fa-solid fa-menorah',
    googleEvent: 'fa-brands fa-google',
    bookingAvailability: 'fa-regular fa-calendar-check',
    hold: 'fa-regular fa-calendar-plus',
    personal: 'fa-solid fa-user-clock',
};

export const ITEM_TYPE_LABELS = {
    event: 'אירוע',
    meeting: 'פגישה',
    task: 'משימה',
    holiday: 'חג',
    googleEvent: 'יומן גוגל',
    bookingAvailability: 'פנוי לתיאום פגישות',
    hold: 'שריון תאריך',
    personal: 'אישי',
};

export function itemIcon(itemType) {
    return ITEM_ICONS[itemType] || 'fa-regular fa-circle';
}

const STYLE_ELEMENT_ID = 'dynamicCalendarColors';

// every PlanupCalendar root
const SCOPE = '.pcal';


// events.planupSystemStatus → the key of its pcSys_ class; none (פניה חדשה) = newLead, which has no
// color of its own since it never reaches the calendar
export function systemStatusKey(planupSystemStatus) {
    return planupSystemStatus ? planupSystemStatus : 'newLead';
}

// only #rrggbb ever reaches the stylesheet
export function validColor(color) {
    return typeof color == 'string' && /^#[0-9a-f]{6}$/i.test(color) ? color : null;
}

export function hexToRgb(color) {
    return [parseInt(color.substr(1, 2), 16), parseInt(color.substr(3, 2), 16), parseInt(color.substr(5, 2), 16)];
}

// "r, g, b" - the value of --pc-rgb
export function rgbTriplet(color) {
    return hexToRgb(color).join(', ');
}

// the YIQ brightness, 0 (black) to 255 (white)
function brightness(rgb) {
    return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
}

// white text on dark colors, dark text on light ones - for a solid fill of the color
export function textColorOn(color) {
    return brightness(hexToRgb(color)) >= 186 ? '#333333' : '#ffffff';
}

// the text color on the color's own tint: the hue itself, darkened until it reads (YIQ 105 is about
// 5:1 on white); a dark hue stays as it is
export function inkOf(color) {
    const rgb = hexToRgb(color);
    const y = brightness(rgb);
    const factor = y > 105 ? 105 / y : 1;
    return '#' + rgb.map(channel => ('0' + Math.round(channel * factor).toString(16)).slice(-2)).join('');
}

export function tintOf(color, alpha) {
    return `rgba(${rgbTriplet(color)}, ${alpha})`;
}

// the striped fill of יצאה הזמנה (rgb = "r, g, b")
export function stripesOf(rgb) {
    return `repeating-linear-gradient(135deg, rgba(${rgb}, .18) 0 4px, rgba(${rgb}, .04) 4px 9px)`;
}

// one color of business_setting.calendarColors (group: systemStatus / eventStatus / meetingType)
export function calendarColorOf(settings, group, key) {
    const colors = settings && settings.calendarColors;
    return validColor(colors && colors[group] && colors[group][key]);
}

// the color a system status is painted with; null = the calendar's own look
export function systemStatusColor(settings, key) {
    if (key == 'hazmanaSigned')
        return validColor(settings && settings.calendarEventColor);
    return calendarColorOf(settings, 'systemStatus', key);
}

// an inline style drawn exactly like the calendar pill of that color
function chipStyle(color, options) {
    const rgb = rgbTriplet(color);
    const style = {
        background: `rgba(${rgb}, ${options.alpha})`,
        color: inkOf(color),
        borderColor: `rgba(${rgb}, ${options.border || 0})`,
        '--pc-rgb': rgb,
        '--pc-ink': inkOf(color),
    };
    if (options.stripes)
        style.backgroundImage = stripesOf(rgb);
    return style;
}

// a chip drawn like the calendar item (the settings page's preview): tinted with the color, or in
// the default look when there is none
export function itemLookStyle(color, look) {
    if (color)
        return chipStyle(color, { alpha: .13 });
    if (look && look.outline)
        return chipStyle(look.outline, { alpha: 0, border: .55 });
    if (look && look.stripes)
        return chipStyle(look.stripes, { alpha: .06, stripes: true });
    return chipStyle((look && look.fill) || '#cccccc', { alpha: .13 });
}


// the items carrying cls, in every view: the variables the pill css reads, forced over the defaults
// of the item's type and status (a chosen color always fills: no outline, no stripes)
function itemColorRule(cls, color) {
    return `${SCOPE} .${cls} { --pc-rgb: ${rgbTriplet(color)} !important; --pc-ink: ${inkOf(color)} !important; `
        + `--pc-alpha: .13 !important; --pc-border-alpha: 0 !important; --pc-stripe: 0 !important; }\n`;
}

// a group of id-keyed colors (eventStatus / meetingType); written after the base rule of its kind -
// same specificity, so the later rule wins
function idColorRules(settings, group, classPrefix) {
    const colors = (settings.calendarColors && settings.calendarColors[group]) || {};
    let css = '';
    Object.keys(colors).forEach(id => {
        const color = validColor(colors[id]);
        if (color && /^\d+$/.test(id))
            css += itemColorRule(classPrefix + id, color);
    });
    return css;
}

// the planup-checkbox colors other pages wear (tasks lists, the customers table)
function checkboxRule(cls, color) {
    return `.planup-checkbox.checked.${cls} { background: ${color} !important; border: 1px solid ${color} !important; }\n`;
}

export function buildCalendarColorsCss(settings) {

    settings = settings || {};
    let css = '';

    // events: by the system status, then by the internal status on top
    CALENDAR_SYSTEM_STATUSES.forEach(key => {
        const color = systemStatusColor(settings, key);
        if (color)
            css += itemColorRule('pcSys_' + key, color);
    });
    css += idColorRules(settings, 'eventStatus', 'pcEventStatus_');

    // meetings: the meeting color, then the meeting type on top
    const meetingColor = validColor(settings.calendarMeetingColor);
    if (meetingColor)
        css += itemColorRule('pcMeeting', meetingColor);
    css += idColorRules(settings, 'meetingType', 'pcMeetingType_');

    const taskColor = validColor(settings.calendarTaskColor);
    if (taskColor)
        css += itemColorRule('pcTask', taskColor);

    // אישי: the type's color, then each user's on top (item-mapper's pcPersonalUser_<users.id>)
    const personalColor = calendarColorOf(settings, 'other', 'personal');
    if (personalColor)
        css += itemColorRule('pcPersonal', personalColor);
    css += idColorRules(settings, 'personalUser', 'pcPersonalUser_');

    css += checkboxRule('planup-checkbox-event', validColor(settings.calendarEventColor) || DEFAULT_EVENT_COLOR);
    css += checkboxRule('planup-checkbox-yellow', taskColor || DEFAULT_TASK_COLOR);
    css += checkboxRule('planup-checkbox-green', meetingColor || DEFAULT_MEETING_COLOR);

    return css;
}

export function applyCalendarColors(settings) {

    let styleElement = document.getElementById(STYLE_ELEMENT_ID);

    if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = STYLE_ELEMENT_ID;
        document.head.appendChild(styleElement);
    }

    styleElement.textContent = buildCalendarColorsCss(settings);
}
